import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next';

import { Dialog } from 'primereact/dialog';
import { Button } from '../../../../components/ui/Button';

import "../../css/layouts.css"

import { useSelector, useDispatch } from 'react-redux';
import { convertToPx, handleSwitchUnit } from '../../../../config/layoutHelper';
import { defaultArea, layoutColor, unitOptions } from '../../../../config/layoutConstants';
import { useCreateLayoutAnimation, useCreateTemplate, useEditScreenAnimation } from '../../../../queries';
import { setUnit } from '../../../../redux/Slices/layoutSlice';
import { toggleDialog } from '../../../../redux/Slices/Global/globalSlice';
import AnimatedAreaControls from './Controls/AnimatedAreaControls';
import DynamicContainer from '../../Layouts/components/Custom/Controls/DynamicContainer';
import LayoutNameController from '../../Layouts/components/Custom/Controls/LayoutNameController';
import { Dropdown } from 'primereact/dropdown';
import { useParams } from 'react-router-dom';
import { AnimationDurations } from './AnimatedLayoutAdsSettings';


function CreateAnimationLayout() {
    const { t } = useTranslation();
    const dispatch = useDispatch()
    const { id: routeId } = useParams();

    const openDialog = useSelector((state) => state.global.openDialog);
    const { selectedLayout, screens_setting, adsSettings, unit, isAnimationScreen, media_delay_duration, layoutAspectRatio } = useSelector((state) => state.layout);

    const isEditAnimatedLayout = routeId != undefined;
    const [layoutName, setLayoutName] = useState({ isEditing: routeId ? false : true, name: routeId ? selectedLayout.title : "" });
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [adsSettingChanged, setAdsSettingChanged] = useState(false);

    const [titleError, setTitleError] = useState("");
    const [endPositionError, setEndPositionError] = useState("");

    const [currentAspectRatio, setCurrentAspectRatio] = useState({});

    const newAreas = [{
        name: routeId ? ("End Position") : "Ad Slot 1",
        id: screens_setting[0]?.id || 1,
        width: screens_setting[0]?.settings?.live.Animated_Width || 100,
        height: screens_setting[0]?.settings?.live.Animated_Height || 100,
        start_margin: screens_setting[0]?.settings?.live.Animated_start_margin || 0,
        top_margin: screens_setting[0]?.settings?.live.Animated_top_margin || 0,
        order: screens_setting[0]?.settings?.live.order || 1,

        unit: "%"
    },
    ...(routeId && Array.isArray(screens_setting[0]?.related_screen)
        ? screens_setting[0]?.related_screen.map((screen, index) => ({
            id: screen.id || index + 2,
            name: screen.name,
            width: screen.width,
            height: screen.height,
            start_margin: screen.start_margin,
            top_margin: screen.top_margin,
            order: screen.settings?.live.order || 1,

            unit: "%"
        }))
        : [])
    ];

    const [areas, setAreas] = useState(newAreas);
    const [isMaximized, setIsMaximized] = useState(true);
    const [endPositionArea, setEndPositionArea] = useState(isEditAnimatedLayout ? areas[0]?.id : null);

    useEffect(() => {
        if (unit.value == "px") {
            const areasConverted = convertToPx(areas, currentAspectRatio);
            setAreas(areasConverted)
        }
        if (unit.value == "%") {
            const areasConverted = handleSwitchUnit(areas, currentAspectRatio);
            setAreas(areasConverted)
        }
    }, [unit])

    const { mutate: createLayout } = useCreateLayoutAnimation();
    const { mutate: editLayout } = useEditScreenAnimation()
    const { mutate: createTemplateMutation, isLoading } = useCreateTemplate();
    const handleCreateLayout = () => {
        if (layoutName.isEditing || !layoutName.name.trim()) {
            setTitleError(t('animated_layouts.title_required'));
            return;
        }

        if (endPositionArea == null) {
            setEndPositionError(t('animated_layouts.end_position_required'));
            return;
        }

        setTitleError("");
        setEndPositionError("");

        handleChange();
    };

    const handleUnitChange = (e) => { dispatch(setUnit(e.target)) }

    const customHeader = (
        <div className="flex justify-between items-center w-full">
            <span className="text-lg font-semibold">{isEditAnimatedLayout ? t('animated_layouts.dialog_edit_title') : t('animated_layouts.dialog_create_title')}</span>
        </div>
    );


    useEffect(() => {
         if (!adsSettings || !selectedLayout?.ads_settings?.live) return;

        const current = selectedLayout.ads_settings.live;
 
        const hasChanged = Object.keys(adsSettings).some(
            key => adsSettings[key] !== current[key]
        );
 
        setAdsSettingChanged(hasChanged);
    }, [adsSettings, selectedLayout]);

    const handleChange = () => {
        const areasConverted = handleSwitchUnit(areas, currentAspectRatio);
        const areaPercentage = areasConverted.map(({ unit, ...rest }) => rest);

        const { advertisement_slots_settings, animated_screen_settings } =
            (areasConverted ?? []).reduce(
                (acc, { unit, id, width, height, start_margin, top_margin, order, ...rest }) => {
                    if (id === endPositionArea) {
                        acc.animated_screen_settings = { width, height, start_margin, top_margin, order };
                    } else {
                        // keep everything except `unit` (already removed) and without touching other props
                        acc.advertisement_slots_settings.push({
                            id, name: rest.name, order,
                            width, height, start_margin, top_margin,
                            ...rest // in case there are extra props you want to keep
                        });
                    }
                    return acc;
                },
                { advertisement_slots_settings: [], animated_screen_settings: null }
            );



        const settings = {
            "is_ads_enabled": true,
            "is_animated_layout": true,
            "ads_delay_duration": adsSettings['ads_delay_duration'] ?? 0,
            "ads_repetition_count": adsSettings['ads_repetition_count'] ?? 0,
            "animation_duration": adsSettings['animation_duration'] ?? 0,
        }

        const defaultScreen = {
            unit: "%",
            id: 1,
            order: animated_screen_settings.order || 1,
            start_margin: 0,
            top_margin: 0,
            width: 100,
            height: 100,
            name: "Main Screen",
        };

        const mutation = routeId ? editLayout : createLayout;
        mutation(
            {
                id: routeId || "",
                data: {
                    title: layoutName.name,
                    aspect_ratio_id: currentAspectRatio.id,
                    ads_settings: settings,
                    screens_setting: [defaultScreen,],
                    media_delay_duration: media_delay_duration,
                    advertisement_slots_number: advertisement_slots_settings.length,
                    advertisement_slots_settings: advertisement_slots_settings,
                    animated_screen_settings: animated_screen_settings,
                },
            },
            {
                onSuccess: () => {
                    dispatch(toggleDialog("CreateAnimationLayout"));
                },
            }
        );
    };

    const [overlappingAreas, setOverlappingAreas] = useState([]);
    const scrollableDivRef = useRef(null);
    const renderFooter = () => {
 
        return (
            <div className="flex justify-between ">
                <div className="text-red-600 text-sm self-center text-start w-50">{endPositionError}</div>
                <div className="items-end w-50 p-1">
                    <Button label={t('buttons.cancel')} className={"   pt-1 pb-1   bg-gray-500 text-white rounded hover:bg-gray-600"} onClick={() => dispatch(toggleDialog("CreateAnimationLayout"))} />
                    <Button loading={isLoading} label={t('buttons.save')} className={"  pt-1 pb-1 "} disabled={isSaveDisabled && !adsSettingChanged||areas.length<1} onClick={() => handleCreateLayout()} />
                </div>
            </div>
        );
    }


    return (
        <Dialog
            id='assignDeviceDialog'

            visible={openDialog.CreateAnimationLayout}
            breakpoints={{ '960px': '95vw' }}
            header={customHeader}
            style={{ overflow: 'hidden' }}
            modal
            footer={renderFooter}
            blockScroll={true}
            className={`p-fluid w-full ${isMaximized ? 'p-dialog-maximized' : 'p-dialog-default md:w-[100%] lg:w-[75%] h-[85%]'}`}
            onHide={() => dispatch(toggleDialog("CreateAnimationLayout"))}
        >
            <div className=" scroll_div overflow-auto h-full">
                <div className="flex flex-col items-center h-full  w-[100%] ">
                    <div className="scroll_div w-100 h-100     pl-5 pr-5 flex flex-col lg:flex-row gap-10 ">
                        {/* Preview Area */}

                        {/* Preview Area */}
                        <DynamicContainer
                            areas={areas}
                            endPositionArea={endPositionArea}

                            setAreas={setAreas}
                            currentAspectRatio={currentAspectRatio}
                            setCurrentAspectRatio={setCurrentAspectRatio}
                            scrollableDivRef={scrollableDivRef}

                        />
                        {/* Controls */}

                        <div className="flex h-100 flex-col w-full lg:w-[40%]  mr-3">
                            <div className="h-[11%] mt-1 flex  w-100 lg:w-1/2">
                                <div className="w-75">
                                    <label className="text-sm font-medium mb-1">{t('animated_layouts.label_title')}</label>
                                    <div className="d-flex mb-3 ">

                                        <LayoutNameController layoutName={layoutName} setLayoutName={setLayoutName} titleError={titleError} />

                                    </div>
                                </div>

                                <div className="w-25">
                                    <label className="text-sm font-medium mb-1">{t('animated_layouts.label_unit')}</label>
                                    <Dropdown
                                        value={unit?.value || unitOptions[0]}
                                        options={unitOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        className="mb-3 mt-1 w-100"
                                        onChange={handleUnitChange}
                                        placeholder={t('animated_layouts.select_unit')}
                                    />
                                </div>

                            </div>
                            <div className="h-[89%]   flex   flex-col w-100 lg:w-1/2">
                                <label className='text-sm pb-2'>{t('animated_layouts.step1_configure')}</label>

                                <AnimatedAreaControls
                                    areas={areas}
                                    setAreas={setAreas}
                                    currentAspectRatio={currentAspectRatio}
                                    overlappingAreas={overlappingAreas}
                                    setOverlappingAreas={setOverlappingAreas}
                                    setIsSaveDisabled={setIsSaveDisabled}
                                    setEndPositionArea={setEndPositionArea}
                                    endPositionArea={endPositionArea}
                                    scrollableDivRef={scrollableDivRef}
                                />

                                <div className="h-[34%]    ">
                                    <label className='text-sm pb-2'>{t('animated_layouts.step2_settings')}</label>
                                    <div className="  rounded border p-2">

                                        <AnimationDurations />
                                    </div>
                                </div>


                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </Dialog >
    )
}

export default CreateAnimationLayout